/**
 * Downsampling utilities for observation charts.
 * Uses fixed intervals (no averaging) - picks the first observation in each time bucket
 * to preserve data accuracy for teams.
 */

const MS_PER_MINUTE = 60 * 1000;

/** Bucket sizes in milliseconds */
export const BUCKET_1_MIN_MS = 1 * MS_PER_MINUTE;
export const BUCKET_10_MIN_MS = 10 * MS_PER_MINUTE;
export const BUCKET_30_MIN_MS = 30 * MS_PER_MINUTE;
export const BUCKET_1_HOUR_MS = 60 * MS_PER_MINUTE;

/** Threshold: ranges >= 6 hours use coarser buckets in auto mode */
export const DOWNSAMPLE_THRESHOLD_6H_MS = 6 * 60 * 60 * 1000;
/** Threshold: ranges >= 24 hours use 30-min buckets in auto mode */
export const DOWNSAMPLE_THRESHOLD_24H_MS = 24 * 60 * 60 * 1000;

export type DownsamplingScale = 'auto' | '1' | '10' | '30' | '60';

/**
 * Returns the bucket size (ms) for automatic scale selection based on range duration.
 * - Up to 6 hours: 1 minute
 * - 6 to 24 hours: 10 minutes
 * - > 24 hours: 30 minutes
 */
export function getAutoBucketMs(rangeDurationMs: number): number {
  if (rangeDurationMs < DOWNSAMPLE_THRESHOLD_6H_MS) return BUCKET_1_MIN_MS;
  if (rangeDurationMs < DOWNSAMPLE_THRESHOLD_24H_MS) return BUCKET_10_MIN_MS;
  return BUCKET_30_MIN_MS;
}

/**
 * Returns the effective bucket size (ms) for the given scale and range.
 */
export function getEffectiveBucketMs(scale: DownsamplingScale, rangeDurationMs: number): number {
  switch (scale) {
    case '1': return BUCKET_1_MIN_MS;
    case '10': return BUCKET_10_MIN_MS;
    case '30': return BUCKET_30_MIN_MS;
    case '60': return BUCKET_1_HOUR_MS;
    case 'auto': return getAutoBucketMs(rangeDurationMs);
    default: return getAutoBucketMs(rangeDurationMs);
  }
}

/**
 * Returns whether downsampling applies (bucket > 1 min). Used by StationCharts.
 */
export function shouldDownsample(rangeDurationMs: number): boolean {
  return getAutoBucketMs(rangeDurationMs) > BUCKET_1_MIN_MS;
}

/**
 * Returns bucket start timestamps (ms) from minMs to maxMs at the given interval.
 */
export function getBucketStarts(minMs: number, maxMs: number, bucketMs: number): number[] {
  const start = Math.floor(minMs / bucketMs) * bucketMs;
  const buckets: number[] = [];
  for (let b = start; b <= maxMs; b += bucketMs) {
    buckets.push(b);
  }
  return buckets;
}
